import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff } from 'lucide-react'
import type { ReactNode } from 'react'
import { UnifiedTimeline } from '@/components/features/comments/UnifiedTimeline'
import { CommentEditor } from '@/components/features/comments/CommentEditor'
import { useComments } from '@/lib/db/rxdb/hooks'
import { cn } from '@/lib/utils'

export interface DiscussionExtraTab {
  id: string
  label: string
  count?: number
  content: ReactNode
}

interface TaskDiscussionStripProps {
  taskId: string
  envId: string
  /** Relations, links and any custom-field tabs; each keeps its own content. */
  extraTabs: Array<DiscussionExtraTab>
  className?: string
  /** Background of the docked composer; must match the surface the strip sits on. */
  dockClassName?: string
}

/**
 * The discussion strip at the foot of a task (design 16): Comments ·
 * Activity · Relations as text tabs, with the composer always in view.
 */
export function TaskDiscussionStrip({
  taskId,
  envId,
  extraTabs,
  className,
  dockClassName = 'bg-card',
}: TaskDiscussionStripProps) {
  const { t } = useTranslation('tasks')
  const [tab, setTab] = useState<string>('discussion')
  const [hideActivity, setHideActivity] = useState(false)
  const { comments } = useComments({ entityId: taskId, entityType: 'work_item', isDeleted: false })
  const commentCount = comments.filter((c) => !c.parentCommentId && c.envId === envId).length

  const tabs: Array<{ id: string; label: string; count?: number }> = [
    // Comments and activity in one stream (design 28a / 30).
    { id: 'discussion', label: t('discussion.label'), count: commentCount },
    ...extraTabs.map(({ id, label, count }) => ({ id, label, count })),
  ]
  const active = tabs.some((item) => item.id === tab) ? tab : 'discussion'
  const extra = extraTabs.find((item) => item.id === active)

  return (
    <section aria-label={t('discussion.label')} className={cn('flex flex-col', className)}>
      <div role="tablist" className="flex items-center gap-4 border-b">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active === item.id}
            onClick={() => setTab(item.id)}
            className={cn(
              '-mb-px flex items-center gap-1.5 border-b-2 py-2 text-[13px] transition-colors',
              active === item.id
                ? 'border-foreground font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {item.label}
            {item.count ? (
              <span
                className={cn(
                  'rounded-full px-1.5 text-[10.5px] font-semibold leading-4 tabular-nums',
                  item.id === 'discussion' ? 'bg-secondary text-muted-foreground' : 'bg-brand text-brand-foreground',
                )}
              >
                {item.count}
              </span>
            ) : null}
          </button>
        ))}
        {active === 'discussion' && (
          <button
            type="button"
            aria-pressed={hideActivity}
            onClick={() => setHideActivity((v) => !v)}
            className="ml-auto flex items-center gap-1.5 py-2 text-[12.5px] text-muted-foreground hover:text-foreground"
          >
            {hideActivity ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
            {hideActivity ? t('discussion.showActivity') : t('discussion.hideActivity')}
          </button>
        )}
      </div>

      <div role="tabpanel" className="pt-3">
        {active === 'discussion' ? (
          <UnifiedTimeline
            key={taskId}
            entityType="work_item"
            entityId={taskId}
            envId={envId}
            view="activity"
            foldActivity
            hideActivity={hideActivity}
            hideHeader
            hideComposer
          />
        ) : (
          extra?.content
        )}
      </div>

      {/* Docked composer: stays in view while the task scrolls above it. */}
      <div
        data-testid="comment-editor-section"
        className={cn('sticky bottom-0 z-10 -mx-1 mt-3 px-1 pb-1 pt-2', dockClassName)}
      >
        <CommentEditor entityType="work_item" entityId={taskId} onCancel={() => {}} />
      </div>
    </section>
  )
}
